import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { getOwnerFacts } from '../lib/api/personal.js';
import { characterImage, DEFAULT_CHARACTER } from '../lib/characters.js';
import { useAccount } from '../lib/useAccount.js';
import { useProfile, useUpdateProfile } from '../lib/useProfile.js';
import './ported/perfil.css';
import { BackIcon } from '../components/chrome/BackIcon.js';
import { IconCamera } from '../components/chrome/icons.js';
import { PhotoOver } from '../components/chrome/PersonPhoto.js';
import { PhotoSheet } from '../components/profile/PhotoSheet.js';
import { useMyPhoto } from '../lib/matrix/photos.js';
import { useMatrixClient } from '../lib/matrix/useMatrix.js';
import { GlyphSmile, GlyphTile } from '../components/ui/Glyphs.js';

export function PerfilPage() {
  const accountQ = useAccount();
  const profileQ = useProfile();
  const update = useUpdateProfile();
  const p = profileQ.data ?? null;
  const factsQ = useQuery({ queryKey: ['owner-facts'], queryFn: getOwnerFacts, staleTime: 30_000 });
  const factsCount = factsQ.data?.facts.length ?? 0;

  const characterKey = p?.characterKey ?? DEFAULT_CHARACTER;
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const [saved, setSaved] = useState(false);
  const { client } = useMatrixClient();
  const photoUrl = useMyPhoto(client);
  const [photoOpen, setPhotoOpen] = useState(false);

  useEffect(() => {
    if (hydrated || !p) return;
    setName((p.nickname || accountQ.data?.user.displayName || '').trim());
    setCity(p.city ?? '');
    setHydrated(true);
  }, [p, accountQ.data, hydrated]);

  async function save() {
    setSaved(false);
    try {
      // nickname = nome do dono (como o secretário se refere a você).
      await update.mutateAsync({ nickname: name.trim() || null, city: city.trim() || null });
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch {
      /* ok */
    }
  }

  const agentName = p?.secretaryName?.trim() || 'seu secretário';

  return (
    <div className="pf">
      <div className="pf-wrap">
        <header className="pf-hd">
          <Link href="/ajustes" className="pf-back back-btn" aria-label="Voltar para Ajustes">
            <BackIcon />
          </Link>
        </header>

        <div className="pf-hero">
          <button
            type="button"
            className={`pf-photo${photoUrl ? ' has' : ''}`}
            onClick={() => setPhotoOpen(true)}
            aria-label={photoUrl ? 'Trocar sua foto' : 'Colocar uma foto sua'}
          >
            <span className="pf-photo-ring">
              <span className="pf-av has-photo-slot">
                <img src={characterImage(characterKey, 'simpatico')} alt="" />
                <PhotoOver url={photoUrl} />
              </span>
            </span>
            {photoUrl && (
              <span className="pf-photo-pal" aria-hidden="true">
                <img src={characterImage(characterKey, 'divertido')} alt="" />
              </span>
            )}
            <span className="pf-photo-cam" aria-hidden="true">
              <IconCamera />
            </span>
          </button>
          <div>
            <div className="pf-kicker">Meu perfil</div>
            <h1 className="pf-h1">{name || 'Você'}</h1>
            <p className="pf-mail">@{accountQ.data?.user.username ?? ''}</p>
          </div>
        </div>

        <section className="pf-card">
          <div className="pf-fld">
            <label>Seu nome</label>
            <input
              value={name}
              maxLength={40}
              onChange={(e) => setName(e.target.value)}
              placeholder="Como o secretário se refere a você"
            />
            <span className="pf-hint">É assim que {agentName} fala de você nas conversas.</span>
          </div>
          <div className="pf-fld">
            <label>
              Cidade <i>opcional</i>
            </label>
            <input
              value={city}
              maxLength={60}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Ex.: Recife, PE"
            />
          </div>
          <div className="pf-fld">
            <label>Telefone do cadastro</label>
            <div className="pf-readonly">{accountQ.data?.user.phoneHint ?? '—'}</div>
            <span className="pf-hint">Pra trocar de número, fale com o suporte.</span>
          </div>
          <button
            className="pf-save"
            onClick={() => void save()}
            disabled={update.isPending || !name.trim()}
          >
            {saved ? 'Salvo! ✓' : update.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </section>

        <Link href="/sobre-voce" className="pf-row">
          <span className="pf-rowe pf-rowe-img">
            <img src="/owner-facts/voce.webp" alt="" />
          </span>
          <div className="pf-rowt">
            <b>Sobre você</b>
            <small>
              {factsCount > 0
                ? `${factsCount} ${factsCount === 1 ? 'coisa que eu sei' : 'coisas que eu sei'} de você`
                : 'Me conta um pouco de você'}
            </small>
          </div>
          <span className="pf-arrow">›</span>
        </Link>

        <Link href="/mudar-jeito" className="pf-row">
          <GlyphTile tone="violet">
            <GlyphSmile />
          </GlyphTile>
          <div className="pf-rowt">
            <b>Mudar o jeito do secretário</b>
            <small>Personagem e personalidade</small>
          </div>
          <span className="pf-arrow">›</span>
        </Link>

        {photoOpen && (
          <PhotoSheet
            client={client}
            currentUrl={photoUrl}
            fallbackSrc={characterImage(characterKey, 'simpatico')}
            onClose={() => setPhotoOpen(false)}
          />
        )}

        <p className="pf-note">
          Seu telefone é usado para entrar e proteger sua conta. Para trocá-lo, fale com o suporte.
        </p>
      </div>
    </div>
  );
}
